import { useEffect, useState } from "react";

import { validateResetToken, verifyEmail } from "../services/auth";

// OUT-949: shared, route-independent extension deeplink / auth handoff.
//
// Transactional emails and extension redirects land on the marketing site root (`/`) carrying query
// params (parsed into AppContext by App.jsx). The site is expected to hand off to the browser
// extension via chrome.runtime.sendMessage. This logic used to live only in pages/Home (mounted at
// /old-hp), so it stopped running once the live `/` route switched to NewHomepage2. It now runs once
// from App.jsx regardless of which homepage variant renders.
//
// Flows handled here:
//   1. Password reset       — rt          -> open-reset-iframe
//   2. Email confirmation   — cc          -> open-reach-tab
//   3. First-campaign onboarding          — isUserNotCreateFirstCampaign -> open-campaigns-user-not-create-first-campaign
//   4. Campaign details deeplink          — (isReadyToLaunchCampaign | isWarnHighBounceRateCampaign) + campaignId -> open-campaign-details-page
//   5. is-extension-installed handshake driving `isExtensionInstalled` (returned; AppLayout shows the
//      "extension not installed" popup when it is false). Gates flows 3 & 4.
//
// Returns `isExtensionInstalled` (null = unknown, true/false once determined).
export function useExtensionDeeplinkHandoff({
  extensionId,
  resetToken,
  confirmationCode,
  isUserNotCreateFirstCampaign,
  isReadyToLaunchCampaign,
  isWarnHighBounceRateCampaign,
  campaignId,
}) {
  const [isExtensionInstalled, setIsExtensionInstalled] = useState(null);

  const sendMessageToExtension = (message) => {
    if (!extensionId) {
      setIsExtensionInstalled(false);
      return;
    }

    try {
      // eslint-disable-next-line no-undef
      chrome.runtime.sendMessage(extensionId, message);
    } catch (error) {
      setIsExtensionInstalled(false);
      console.log(`Error: ${error.message}`);
    }
  };

  const isOpenCampaignsPage = isUserNotCreateFirstCampaign;
  const isOpenCampaignDetailsPage =
    (isReadyToLaunchCampaign || isWarnHighBounceRateCampaign) && campaignId;
  const isExtensionCheckRequired =
    isOpenCampaignsPage || isOpenCampaignDetailsPage;

  // 1. Password reset
  useEffect(() => {
    if (resetToken && extensionId) {
      const validateToken = async () => {
        const response = await validateResetToken(resetToken);
        if (!response.success) {
          sendMessageToExtension({
            message: "show-error-message",
            data: {
              message:
                "We’re sorry, but we’re unable to reset your password. Please try again",
            },
          });
          return;
        }
        sendMessageToExtension({
          message: "open-reset-iframe",
          data: {
            invited: response.invited,
            resetToken,
            windowType: "popup",
          },
        });

        const url = new URL(window.location.href);
        url.searchParams.delete("rt");
        window.history.replaceState({}, "", url);
      };

      validateToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetToken]);

  // 2. Email confirmation
  useEffect(() => {
    if (confirmationCode && extensionId) {
      const verifyUser = async () => {
        const response = await verifyEmail({ confirmationCode });
        if (!response.success) {
          sendMessageToExtension({
            message: "show-error-message",
            data: {
              message:
                response.message ||
                "We’re sorry, but we’re unable to confirm your email. Please try again",
            },
          });
          return;
        }
        sendMessageToExtension({
          message: "open-reach-tab",
        });
      };

      verifyUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmationCode]);

  // 5. is-extension-installed handshake (gates flows 3 & 4)
  useEffect(() => {
    if (!isExtensionCheckRequired) {
      return undefined;
    }

    const postMessageEventHandler = (event) => {
      if (event?.data?.isExtensionInstalled !== undefined) {
        setIsExtensionInstalled(event.data.isExtensionInstalled);
      }
    };

    window.addEventListener("message", postMessageEventHandler);

    try {
      sendMessageToExtension({
        message: "is-extension-installed",
      });
    } catch {
      //
    }

    return () => {
      window.removeEventListener("message", postMessageEventHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExtensionCheckRequired]);

  // 3 & 4. Open campaigns list / campaign details once the extension is confirmed installed
  useEffect(() => {
    if (isExtensionInstalled) {
      if (isOpenCampaignsPage) {
        sendMessageToExtension({
          message: "open-campaigns-user-not-create-first-campaign",
        });
      } else if (isOpenCampaignDetailsPage) {
        sendMessageToExtension({
          message: "open-campaign-details-page",
          data: {
            campaignId,
          },
        });
      }

      window.history.replaceState(
        null,
        document.title,
        window.location.pathname
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExtensionInstalled]);

  return isExtensionInstalled;
}
